import { Tooltip } from '@material-ui/core';
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';

import { SidebarEventTypes } from '~/src/dash/analytics/analytics';
import { BetaTooltipIcon } from '~/src/dash/components/icons/beta-icon';
import { FONT_WEIGHT } from '~/src/dash/components/text/common';
import { Col, Cols, Text, TKey, usePanel, usePublicaTranslation } from '~/src/dash/publicaUI';
import { getOversizedLanguageUIFriendlyStyle } from '~/src/dash/publicaUI/common';
import { extractRouteParams, getRoutePath } from '~/src/dash/router/routes';
import { useBaseState, useBaseStore } from '~/src/dash/views/base/contexts';
import { useNavBarStore } from '~/src/dash/views/navbar/contexts';
import { getMenuBackgroundColor, trackEvent } from '~/src/dash/views/navbar/NavBar/common/consts';
import { IRoute } from '~/src/dash/views/navbar/NavBar/common/types';

interface INavBarRowProps {
  label: string;
  iconPath: string;
  eventName: SidebarEventTypes;
  isExternalRoute?: boolean;
  route: IRoute;
  tooltipText: React.ReactNode;
  isDisabled: boolean | undefined;
  iconPathDark: string;
  customOnClick?: () => void;
  isBeta?: boolean | undefined;
  tKey?: TKey;
  uiFriendlyTKey?: TKey; // used if a string needs to be modified to better fit the ui
}

export const NavBarRow = ({
  label,
  iconPath,
  eventName,
  isExternalRoute,
  route,
  tooltipText,
  isDisabled,
  iconPathDark,
  customOnClick,
  isBeta = false,
  tKey,
  uiFriendlyTKey,
}: INavBarRowProps) => {
  const { theme } = usePanel();
  const { session, user, location } = useBaseStore();
  const { isNavExpanded } = useBaseState();
  const { isDarkTheme } = useNavBarStore();
  const { i18n } = usePublicaTranslation();
  const [isHover, setIsHover] = useState(false);
  const path = getRoutePath(
    route,
    extractRouteParams({
      session,
      user,
    }),
  );
  const isActive = location.pathname.startsWith(path);
  const isSelectedDark = (isActive || isHover) && isDarkTheme;

  const handleLinkClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isDisabled) {
      e.preventDefault();
    } else {
      trackEvent(eventName, user);
      customOnClick && customOnClick();
    }
  };

  // a render is NOT the preferred route, but this circumvents the need to duplicate code here
  // we abstract this away to clean up the <NavLink> or <a> logic
  const renderLinkItem = () => (
    <Tooltip
      title={isNavExpanded ? '' : tooltipText || label}
      placement='right'
      disableHoverListener={isNavExpanded}
    >
      <button
        style={{
          padding: 0,
          margin: 0,
          width: '100%',
          border: 'none',
          borderRadius: '4px',
          backgroundColor: isDisabled
            ? 'inherit'
            : getMenuBackgroundColor(isHover, isActive, theme),
          fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
        }}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        <Cols
          style={{
            minHeight: '32px',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            cursor: isDisabled ? 'default' : 'pointer',
          }}
        >
          <Col
            style={{
              padding: '10px 8px 6px 8px',
              width: '32px',
            }}
          >
            <img
              src={isSelectedDark ? iconPathDark : iconPath}
              width='16px'
              height='16px'
              style={{ opacity: isDisabled ? 0.7 : 1 }}
            />
          </Col>
          {isNavExpanded && (
            <Col style={{ padding: '6px 8px 2px 8px' }}>
              <Text
                color={isSelectedDark ? '#000' : theme.menuBarText}
                style={{
                  textAlign: 'start',
                  verticalAlign: 'middle',
                  fontWeight: isActive ? FONT_WEIGHT.bold : FONT_WEIGHT.base,
                  opacity: isDisabled ? 0.7 : 1,
                  ...getOversizedLanguageUIFriendlyStyle(i18n.language),
                }}
                tKey={uiFriendlyTKey || tKey}
              >
                {label}
              </Text>
            </Col>
          )}
          {isBeta && (
            <Col displayFlex>
              <BetaTooltipIcon />
            </Col>
          )}
        </Cols>
      </button>
    </Tooltip>
  );

  return (
    <>
      {isExternalRoute ? (
        <a
          href={route.path}
          target='_blank'
          onClick={handleLinkClick}
          style={{ textDecoration: 'none' }}
        >
          {renderLinkItem()}
        </a>
      ) : (
        <NavLink onClick={handleLinkClick} to={path} style={{ textDecoration: 'none' }}>
          {renderLinkItem()}
        </NavLink>
      )}
    </>
  );
};
